import { useOverlayPortalRoot } from "@/components/modal/overlay.hooks";
import {
  CHALK_ADMIN_SURFACE_CLASS,
  CHALK_ADMIN_SURFACE_HOVER_CLASS,
  CHALK_ADMIN_SURFACE_STABLE_CLASS,
  CHALK_DANGER_SURFACE_CLASS,
  CHALK_DANGER_SURFACE_HOVER_CLASS,
  CHALK_DANGER_SURFACE_STABLE_CLASS,
} from "@/components/admin/admin-visual-treatment";
import { useRedSurfacesSuppressed } from "@/components/admin/use-admin-surface";
import { Icon, IconName } from "@/components/icons/Icon";
import { IconButtonV2 } from "@/components/interactive/ButtonV2";
import { CountBadgeV2 } from "@/components/interactive/CountBadgeV2";
import { InputV2 } from "@/components/interactive/InputV2";
import { MenuContainerV2, MenuSectionV2 } from "@/components/interactive/MenuContainerV2";
import { MenuItemV2 } from "@/components/interactive/MenuItemV2";
import {
  SidebarToggleShortcutTooltip,
  type SidebarToggleShortcutHint,
} from "@/components/interactive/SidebarToggleShortcutTooltip";
import { SideNavSectionTitleV2 } from "@/components/interactive/SideNavSectionTitleV2";
import { ViewportAwareFixedMenuV2 } from "@/components/interactive/ViewportAwareFixedMenuV2";
import { TooltipV2 } from "@/components/TooltipV2";
import { ZIndex, ZIndexClass } from "@/constants/z-index";
import { cx } from "@/utils/classnames";
import {
  type DragReorderExternalTargets,
  useDragReorder,
  useDragReorderLists,
} from "@/utils/hooks/use-drag-reorder";
import {
  usePersistentScrollPosition,
  usePersistentScrollTop,
} from "@/utils/hooks/use-persistent-scroll-position";
import { ChalkLink } from "@/components/interactive/ChalkLink";
import { type LinkProps } from "next/link";
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type SideNavV2Icon = IconName;
export type SideNavV2Theme = "light" | "dark";

/** A command in a row's "…" menu, which appears while the row is hovered. */
export interface SideNavV2ItemAction {
  id: string;
  label: string;
  icon?: SideNavV2Icon;
  onSelect: () => void;
}

export interface SideNavV2Item<T extends string = string> {
  id: T;
  label: string;
  href?: LinkProps["href"];
  shouldOpenInNewTab?: boolean;
  icon?: SideNavV2Icon;
  description?: string;
  /** Marks the destination as deprecated: the label renders struck-through and dimmed, the
   *  destination is not navigable, and hovering the item shows this notice. */
  deprecationNotice?: string;
  count?: string | number;
  menuGroupLabel?: string;
  admin?: boolean;
  /** `subtle` quiets a row that leads out of the sidebar's own pages, such as Back to app. */
  visualTone?: "danger" | "subtle";
  disabled?: boolean;
  /** Prevent navigation while selected, even when URL search params differ from the href. */
  allowNavigationWhenSelected?: boolean;
  onSelect?: () => void;
  childrenDisplay?: "inline" | "menu";
  children?: readonly SideNavV2Item<T>[];
  actions?: readonly SideNavV2ItemAction[];
  /** Wraps the row to show a preview while it is hovered, such as an entity preview card. */
  renderHoverPreview?: (row: React.ReactElement) => React.ReactElement;
}

export interface SideNavV2Section<T extends string = string> {
  id: string;
  label?: string;
  hidden?: boolean;
  /** Turns the section title into a toggle that collapses and expands the section's items. */
  collapsible?: boolean;
  /** A collapsible section starts collapsed, unless it holds the active item. */
  defaultCollapsed?: boolean;
  /** Shows a collapsible title's chevron only while the title is hovered or focused. */
  revealChevronOnHover?: boolean;
  /** Keeps its rows in place when the nav lets readers reorder rows, as for a list ordered elsewhere. */
  fixedRows?: boolean;
  items: readonly SideNavV2Item<T>[];
}

export interface SideNavV2WorkspaceMenuItem {
  id: string;
  label: string;
  href?: LinkProps["href"];
  icon?: SideNavV2Icon;
  count?: string | number;
  admin?: boolean;
  onSelect?: () => void;
  /** Nested rows, opened as a hover submenu to the right. A row with
   *  children is a submenu host only: its own `onSelect`/`href` is ignored. */
  children?: readonly SideNavV2WorkspaceMenuItem[];
}

export interface SideNavV2Props<T extends string = string>
  extends Omit<React.ComponentProps<"nav">, "children" | "onSelect"> {
  workspaceLabel: string;
  workspaceIcon?: SideNavV2Icon;
  headerVariant?: "workspace" | "back";
  showHeader?: boolean;
  backLabel?: string;
  backHref?: LinkProps["href"];
  sections: readonly SideNavV2Section<T>[];
  activeItemId?: T;
  /** Runs before an item's own command, useful for synchronizing shell state before navigation. */
  onBeforeItemSelect?: (item: SideNavV2Item<T>) => void;
  onItemSelect?: (item: SideNavV2Item<T>) => void;
  onWorkspaceSelect?: () => void;
  workspaceMenuItems?: readonly SideNavV2WorkspaceMenuItem[];
  headerAccessory?: React.ReactNode;
  footerAccessory?: React.ReactNode;
  onBackToApp?: () => void;
  onToggleCollapse?: () => void;
  showToggleButton?: boolean;
  toggleButtonIcon?: SideNavV2Icon;
  toggleButtonLabel?: string;
  toggleTooltipLabel?: string;
  toggleTooltipShortcut?: SidebarToggleShortcutHint;
  hoverMenuOpenDelayMs?: number;
  /** Temporarily disables hover-triggered flyouts while the sidebar geometry is transitioning. */
  hoverMenusDisabled?: boolean;
  showDetailPanes?: boolean;
  scrollPositionKey?: string;
  currentPath?: string;
  themeMode?: SideNavV2Theme;
  /**
   * Collapsed sections, for callers that own collapse state (to persist it, or to apply it to
   * sections that load after mount). Toggles then only report through `onSectionCollapsedChange`.
   */
  collapsedSectionIds?: ReadonlySet<string>;
  /** Reports a reader collapsing or expanding a collapsible section. */
  onSectionCollapsedChange?: (sectionId: string, collapsed: boolean) => void;
  /**
   * Lets the reader drag titled sections into a new order by a handle on each title, and reports
   * every section's id in that order. Untitled sections leading the list stay put. The caller
   * owns the order and passes `sections` in it.
   */
  onReorderSections?: (sectionIds: readonly string[]) => void;
  /**
   * Lets the reader drag rows into a new order within their section or into another section, and
   * reports every such section's rows in their new arrangement. Sections marked `fixedRows` neither
   * give nor take rows, and collapsed ones take none. The caller owns the arrangement and passes
   * each section's `items` in it.
   */
  onArrangeRows?: (
    arrangement: readonly { sectionId: string; itemIds: readonly string[] }[],
  ) => void;
  /**
   * With any of these, a reorderable section's title has a "…" menu beside its grip: Rename edits
   * the title in place, New section below adds an empty section after it (returning its id, which
   * then opens for renaming), and Delete section, offered once the section has no rows, removes
   * it. Rows are never deleted. Sections marked `fixedRows` keep their titles.
   */
  /**
   * Reports a section or row drag starting and ending, for hosts that must stay open while the
   * pointer is away dragging, such as a hover flyout.
   */
  onReorderDragChange?: (dragging: boolean) => void;
  onRenameSection?: (sectionId: string, label: string) => void;
  onCreateSection?: (afterSectionId: string) => string | undefined;
  onDeleteSection?: (sectionId: string) => void;
  /**
   * Places outside the nav a dragged row can drop on instead, such as the category rail's icons.
   * Over one, the rows go back to where they were and the lifted row dims. Needs `onArrangeRows`.
   */
  rowDropTargets?: DragReorderExternalTargets;
  /**
   * Places outside the nav a dragged section can drop on instead, as `rowDropTargets` are for rows.
   * Needs `onReorderSections`.
   */
  sectionDropTargets?: DragReorderExternalTargets;
  /** Reports a row's "…" menu opening or closing, for hosts that must stay open beneath it. */
  onItemActionsMenuOpenChange?: (open: boolean) => void;
  /** The canvas the nav sits on: the recessed backing canvas, or an elevated pane. */
  surface?: "backing" | "elevated";
}

const sectionHoldsItem = <T extends string>(
  items: readonly SideNavV2Item<T>[],
  itemId: T | undefined,
): boolean =>
  itemId != null &&
  items.some((item) => item.id === itemId || sectionHoldsItem(item.children ?? [], itemId));

const SideNavCollapsibleSectionTitle: React.FC<{
  label: string;
  collapsed: boolean;
  controlsId: string;
  revealChevronOnHover?: boolean;
  /** Leaves room at the right for a drag handle, so the chevron sits beside it. */
  besideDragHandle?: boolean;
  onToggle: () => void;
}> = ({
  label,
  collapsed,
  controlsId,
  revealChevronOnHover = false,
  besideDragHandle = false,
  onToggle,
}) => (
  <button
    type={"button"}
    aria-expanded={!collapsed}
    aria-controls={controlsId}
    onClick={onToggle}
    className={cx(
      "group/section-title flex h-7 w-full cursor-pointer items-center justify-between rounded-cds-6 pb-cds-4 pl-cds-8 pr-cds-4 pt-cds-8 text-left text-cds-xs",
      "text-cds-ink-subtle hover:text-cds-ink dark:hover:text-zinc-200",
      besideDragHandle && "pr-cds-24",
    )}
  >
    <span>{label}</span>
    <Icon
      name={collapsed ? "chevronRight" : "chevronDown"}
      size={"xs"}
      aria-hidden={true}
      className={cx(
        "shrink-0",
        revealChevronOnHover &&
          "opacity-0 transition-opacity duration-75 group-hover/section-title:opacity-100 " +
            "group-focus-visible/section-title:opacity-100",
      )}
    />
  </button>
);

/**
 * What hovering a row's or a section title's grip says. Beside the sidebar, so it covers no rows,
 * and hidden while the grip's row or section is dragged.
 */
const SideNavDragHandleTooltip: React.FC<{ lifted: boolean; children: React.ReactElement }> = ({
  lifted,
  children,
}) => (
  <TooltipV2
    content={"Drag to rearrange"}
    placement={"right"}
    persistOnHover={false}
    suppressOpen={lifted}
  >
    {children}
  </TooltipV2>
);

/**
 * The grip on a reorderable section's title, at its right end and level with its text. It shows
 * while the title is hovered, and stays while its section is dragged.
 */
const SideNavSectionDragHandle: React.FC<{
  lifted: boolean;
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
}> = ({ lifted, onPointerDown }) => (
  <SideNavDragHandleTooltip lifted={lifted}>
    <span
      aria-hidden={true}
      data-sidenav-section-drag-handle={""}
      onPointerDown={onPointerDown}
      className={cx(
        "absolute right-cds-4 top-cds-8 flex h-4 w-4 cursor-grab items-center justify-center",
        "text-cds-ink-subtle transition-opacity duration-75 hover:text-cds-ink dark:hover:text-zinc-200",
        lifted ? "opacity-100" : "opacity-0 group-hover/section-heading:opacity-100",
      )}
    >
      <Icon name={"gripVertical"} size={"xs"} aria-hidden={true} />
    </span>
  </SideNavDragHandleTooltip>
);

const getSideNavEntryId = (entry: { id: string }) => entry.id;

/**
 * A section title being renamed: InputV2, in place of the title and as tall, 26px with a 1px margin
 * above and below so its focus ring stays clear of the edges that clip the section. Enter or
 * leaving it keeps the new title, Escape the old one.
 */
const SideNavSectionTitleInput: React.FC<{
  label: string;
  onCommit: (label: string) => void;
  onCancel: () => void;
}> = ({ label, onCommit, onCancel }) => {
  const [value, setValue] = useState(label);
  const doneRef = useRef(false);
  const finish = (commit: boolean) => {
    if (doneRef.current) return;
    doneRef.current = true;
    if (commit) onCommit(value);
    else onCancel();
  };

  return (
    <InputV2
      size={"sm"}
      label={"Section title"}
      hideLabel
      value={value}
      onChange={(event) => setValue(event.currentTarget.value)}
      maxLength={48}
      containerClassName={"!h-[26px] my-px"}
      // Opened from the title's own menu, so taking focus is what the reader asked for.
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      onFocus={(event) => event.currentTarget.select()}
      onKeyDown={(event) => {
        if (event.key === "Enter") finish(true);
        if (event.key === "Escape") finish(false);
      }}
      onBlur={() => finish(true)}
    />
  );
};

/**
 * Where a dragged section or row would land: its silhouette, the size and shape of what is
 * lifted, filled as BasicTableV3's column menu fills a section it drops into and outlined in that
 * menu's drop stroke. It is its list's first child, so the lifted entry, and any sliding past,
 * paints over it.
 */
const SideNavDropSlot: React.FC<{ kind: "section" | "row"; top: number; height: number }> = ({
  kind,
  top,
  height,
}) => (
  <div
    aria-hidden={true}
    data-sidenav-drop-slot={kind}
    className={cx(
      "pointer-events-none absolute",
      // Both span the list's 5px padding; rows keep their own hover corners.
      kind === "section" ? "inset-x-[5px] rounded-cds-6" : "inset-x-[5px] rounded-cds-3",
      "border-[1px] border-dashed border-cds-primary-stroke-default",
      "bg-cds-surface-hover dark:bg-white/[0.06]",
    )}
    style={{ top, height }}
  />
);

const SIDENAV_WIDTH = "w-[200px]";
const SIDENAV_BODY_PADDING_CLASS = "px-[5px]";
const SIDENAV_ITEM_GAP_CLASS = "gap-cds-2";
// Right padding is applied per-row, mutually exclusively: rows with a trailing
// affordance (count badge, submenu chevron) pull in to 6px like MenuItemV2's
// trailing accessories, others keep 8px. The layout class must not set pr —
// both paddings are !important utilities of equal specificity, so stacking
// them lets Tailwind's stylesheet order (pr-cds-8 after pr-cds-6) win, not the
// intent.
const SIDENAV_RIGHT_AFFORDANCE_PADDING_CLASS = "!pr-cds-6";
const SIDENAV_DEFAULT_RIGHT_PADDING_CLASS = "!pr-cds-8";
const SIDENAV_ITEM_LAYOUT_CLASS = "!h-7 !rounded-cds-3 !gap-cds-8 !pl-cds-8";
const SIDENAV_ITEM_HIT_TARGET_CLASS =
  "absolute -inset-y-[2px] -left-[5px] -right-[5px] z-20 rounded-cds-3";
// Item styling never encodes a theme decision: every utility is a light value
// paired with a `dark:` counterpart, and which one wins is settled by the cascade
// from the nearest `dark` / `theme-light` ancestor. SideNavV2 puts that class on
// the nav root and on each flyout portal, so it reaches every row.
//
// This has to stay CSS-resolved rather than JS-resolved. next-themes only knows
// the theme in the browser, so a palette chosen at render time is a guess during
// SSR and first paint — it paints one theme's colors under the other theme's
// shell until hydration replaces them.
//
// Two rules when editing these:
//   - Write `dark:` utilities out literally. Tailwind's scanner cannot see class
//     names assembled by string manipulation, so a computed variant is silently
//     never generated.
//   - Pair per property. Both sides are `!important` and the darkMode selector is
//     wrapped in `:where()`, which contributes no specificity, so a light utility
//     left without a `dark:` partner leaks into dark mode.
const SIDENAV_ITEM_TONE_CLASS =
  // The dark side restates MenuItemV2's own dark defaults for label-hover and icon
  // colors rather than introducing values that would drift from it.
  "!text-cds-ink dark:!text-zinc-300 " +
  "hover:!bg-zinc-200/60 dark:hover:!bg-white/[0.06] " +
  "hover:!text-zinc-950 dark:hover:!text-white " +
  "[&_.menu-item-v2-left-icon]:!text-cds-ink-subtle " +
  "dark:[&_.menu-item-v2-left-icon]:!text-zinc-400 " +
  "[&:hover_.menu-item-v2-left-icon]:!text-zinc-800 " +
  "dark:[&:hover_.menu-item-v2-left-icon]:!text-white";

const SIDENAV_ITEM_CLASS = `${SIDENAV_ITEM_LAYOUT_CLASS} ${SIDENAV_ITEM_TONE_CLASS}`;

// A `subtle` row reads quieter than the pages around it, in the back header's colors
// (SideNavHeaderV2): its label and icon sit a step below theirs at rest and on hover. It replaces
// the regular tone and hover classes rather than overriding them, which would tie on specificity.
const SIDENAV_ITEM_SUBTLE_TONE_CLASS =
  "!text-cds-ink-subtle dark:!text-zinc-500 " +
  "hover:!bg-zinc-200/60 dark:hover:!bg-white/[0.06] " +
  "hover:!text-cds-ink-secondary dark:hover:!text-zinc-400 " +
  "[&_.menu-item-v2-left-icon]:!text-zinc-400 " +
  "dark:[&_.menu-item-v2-left-icon]:!text-zinc-500 " +
  "[&:hover_.menu-item-v2-left-icon]:!text-cds-ink-secondary " +
  "dark:[&:hover_.menu-item-v2-left-icon]:!text-zinc-400";

const SIDENAV_ITEM_SUBTLE_CLASS = `${SIDENAV_ITEM_LAYOUT_CLASS} ${SIDENAV_ITEM_SUBTLE_TONE_CLASS}`;

const SIDENAV_ITEM_ACTIVE_CLASS =
  "!cursor-default !bg-zinc-200 dark:!bg-white/[0.12] " +
  "!text-zinc-950 dark:!text-white " +
  "hover:!bg-zinc-200 dark:hover:!bg-white/[0.12] " +
  "[&_.menu-item-v2-left-icon]:!text-zinc-800 " +
  "dark:[&_.menu-item-v2-left-icon]:!text-white " +
  "[&:hover_.menu-item-v2-left-icon]:!text-zinc-800 " +
  "dark:[&:hover_.menu-item-v2-left-icon]:!text-white";

const SIDENAV_ITEM_DESCENDANT_ACTIVE_CLASS =
  "!cursor-default !bg-zinc-200/60 dark:!bg-white/[0.06] " +
  "!text-zinc-950 dark:!text-white " +
  "hover:!bg-zinc-200/60 dark:hover:!bg-white/[0.06] " +
  "[&_.menu-item-v2-left-icon]:!text-zinc-800 " +
  "dark:[&_.menu-item-v2-left-icon]:!text-white " +
  "[&:hover_.menu-item-v2-left-icon]:!text-zinc-800 " +
  "dark:[&:hover_.menu-item-v2-left-icon]:!text-white";

const SIDENAV_ITEM_HOVER_MENU_OPEN_CLASS =
  "!bg-zinc-200/60 dark:!bg-white/[0.06] " +
  "!text-zinc-950 dark:!text-white " +
  "[&_.menu-item-v2-left-icon]:!text-zinc-800 " +
  "dark:[&_.menu-item-v2-left-icon]:!text-white";

const SIDENAV_ITEM_GROUP_HOVER_CLASS =
  "group-hover/sidenav-hit:!bg-zinc-200/60 dark:group-hover/sidenav-hit:!bg-white/[0.06] " +
  "group-hover/sidenav-hit:!text-zinc-950 dark:group-hover/sidenav-hit:!text-white " +
  "group-hover/sidenav-hit:[&_.menu-item-v2-left-icon]:!text-zinc-800 " +
  "dark:group-hover/sidenav-hit:[&_.menu-item-v2-left-icon]:!text-white " +
  "group-focus-within/sidenav-hit:!bg-zinc-200/60 " +
  "dark:group-focus-within/sidenav-hit:!bg-white/[0.06] " +
  "group-focus-within/sidenav-hit:!text-zinc-950 " +
  "dark:group-focus-within/sidenav-hit:!text-white " +
  "group-focus-within/sidenav-hit:[&_.menu-item-v2-left-icon]:!text-zinc-800 " +
  "dark:group-focus-within/sidenav-hit:[&_.menu-item-v2-left-icon]:!text-white";

const SIDENAV_ITEM_SUBTLE_GROUP_HOVER_CLASS =
  "group-hover/sidenav-hit:!bg-zinc-200/60 dark:group-hover/sidenav-hit:!bg-white/[0.06] " +
  "group-hover/sidenav-hit:!text-cds-ink-secondary dark:group-hover/sidenav-hit:!text-zinc-400 " +
  "group-hover/sidenav-hit:[&_.menu-item-v2-left-icon]:!text-cds-ink-secondary " +
  "dark:group-hover/sidenav-hit:[&_.menu-item-v2-left-icon]:!text-zinc-400 " +
  "group-focus-within/sidenav-hit:!bg-zinc-200/60 " +
  "dark:group-focus-within/sidenav-hit:!bg-white/[0.06] " +
  "group-focus-within/sidenav-hit:!text-cds-ink-secondary " +
  "dark:group-focus-within/sidenav-hit:!text-zinc-400 " +
  "group-focus-within/sidenav-hit:[&_.menu-item-v2-left-icon]:!text-cds-ink-secondary " +
  "dark:group-focus-within/sidenav-hit:[&_.menu-item-v2-left-icon]:!text-zinc-400";

const SIDENAV_CHILD_ITEM_GROUP_HOVER_CLASS =
  "group-hover/sidenav-child-hit:!bg-zinc-200/60 " +
  "dark:group-hover/sidenav-child-hit:!bg-white/[0.06] " +
  "group-hover/sidenav-child-hit:!text-zinc-950 " +
  "dark:group-hover/sidenav-child-hit:!text-white " +
  "group-hover/sidenav-child-hit:[&_.menu-item-v2-left-icon]:!text-zinc-800 " +
  "dark:group-hover/sidenav-child-hit:[&_.menu-item-v2-left-icon]:!text-white " +
  "group-focus-within/sidenav-child-hit:!bg-zinc-200/60 " +
  "dark:group-focus-within/sidenav-child-hit:!bg-white/[0.06] " +
  "group-focus-within/sidenav-child-hit:!text-zinc-950 " +
  "dark:group-focus-within/sidenav-child-hit:!text-white " +
  "group-focus-within/sidenav-child-hit:[&_.menu-item-v2-left-icon]:!text-zinc-800 " +
  "dark:group-focus-within/sidenav-child-hit:[&_.menu-item-v2-left-icon]:!text-white";

// Child rows deliberately share the parent row's tone and active treatment; only
// their hover group and danger tone differ.
const SIDENAV_CHILD_ITEM_CLASS = SIDENAV_ITEM_CLASS;
const SIDENAV_CHILD_ITEM_ACTIVE_CLASS = SIDENAV_ITEM_ACTIVE_CLASS;

const SIDENAV_CHILD_ITEM_DANGER_CLASS =
  "!bg-red-500/[0.12] dark:!bg-red-500/[0.16] " +
  "group-hover/sidenav-child-hit:!bg-red-500/[0.18] " +
  "dark:group-hover/sidenav-child-hit:!bg-red-500/[0.22] " +
  "group-focus-within/sidenav-child-hit:!bg-red-500/[0.18] " +
  "dark:group-focus-within/sidenav-child-hit:!bg-red-500/[0.22]";

const SIDENAV_CHILD_ITEM_DANGER_ACTIVE_CLASS =
  "!cursor-default !bg-red-500/[0.18] dark:!bg-red-500/[0.22] " +
  "!text-zinc-950 dark:!text-white " +
  "hover:!bg-red-500/[0.18] dark:hover:!bg-red-500/[0.22] " +
  "[&_.menu-item-v2-left-icon]:!text-zinc-800 " +
  "dark:[&_.menu-item-v2-left-icon]:!text-white " +
  "[&:hover_.menu-item-v2-left-icon]:!text-zinc-800 " +
  "dark:[&:hover_.menu-item-v2-left-icon]:!text-white";

const SIDENAV_DISCLOSURE_ICON_CLASS = cx(
  "!text-zinc-400 dark:!text-zinc-600 group-hover/sidenav-hit:!text-zinc-500",
  "group-focus-within/sidenav-hit:!text-zinc-500 dark:group-hover/sidenav-hit:!text-zinc-500",
  "dark:group-focus-within/sidenav-hit:!text-zinc-500 origin-center [rotate:0deg] [scale:1]",
  "transition-[color,rotate,scale,opacity] [transition-duration:150ms,150ms,500ms,500ms]",
  "ease-out will-change-transform",
);

const SIDENAV_DISCLOSURE_ICON_OPEN_CLASS = cx(
  "!text-zinc-500 dark:!text-zinc-500",
  "origin-center [rotate:90deg] [scale:0] opacity-0",
  "transition-[color,rotate,scale,opacity] [transition-duration:150ms,150ms,500ms,500ms]",
  "[transition-delay:0ms,0ms,150ms,150ms] ease-out will-change-transform",
);

// suppressRedSurfaces: in demo mode ALL red treatments (admin tint and danger
// tone alike) are skipped entirely — not just repainted by CSS — so the item
// renders exactly like a normal sibling, standard hover/active highlight
// included. See useRedSurfacesSuppressed.
const getSideNavItemVisualTreatmentClass = (
  item: Pick<SideNavV2Item, "admin" | "visualTone">,
  suppressRedSurfaces: boolean,
) =>
  suppressRedSurfaces
    ? undefined
    : item.visualTone === "danger"
      ? CHALK_DANGER_SURFACE_CLASS
      : item.admin
        ? CHALK_ADMIN_SURFACE_CLASS
        : undefined;

const getSideNavItemVisualTreatmentStableClass = (
  item: Pick<SideNavV2Item, "admin" | "visualTone">,
  suppressRedSurfaces: boolean,
) =>
  suppressRedSurfaces
    ? undefined
    : item.visualTone === "danger"
      ? CHALK_DANGER_SURFACE_STABLE_CLASS
      : item.admin
        ? CHALK_ADMIN_SURFACE_STABLE_CLASS
        : undefined;

const getSideNavItemVisualTreatmentHoverClass = (
  item: Pick<SideNavV2Item, "admin" | "visualTone">,
  suppressRedSurfaces: boolean,
) =>
  suppressRedSurfaces
    ? undefined
    : item.visualTone === "danger"
      ? CHALK_DANGER_SURFACE_HOVER_CLASS
      : item.admin
        ? CHALK_ADMIN_SURFACE_HOVER_CLASS
        : undefined;

const SIDENAV_SCROLLBAR_CLASS =
  "[scrollbar-color:rgba(113,113,122,0.28)_transparent] " +
  "dark:[scrollbar-color:rgba(255,255,255,0.16)_transparent] [scrollbar-width:thin] " +
  "[&::-webkit-scrollbar]:!h-1.5 [&::-webkit-scrollbar]:!w-1.5 " +
  "[&::-webkit-scrollbar-track]:!bg-transparent " +
  "[&::-webkit-scrollbar-thumb]:!rounded-full " +
  "[&::-webkit-scrollbar-thumb]:!bg-zinc-400/30 " +
  "dark:[&::-webkit-scrollbar-thumb]:!bg-white/[0.16] " +
  "[&::-webkit-scrollbar-thumb:hover]:!bg-zinc-500/40 " +
  "dark:[&::-webkit-scrollbar-thumb:hover]:!bg-white/[0.28]";

const SIDENAV_HOVER_MENU_GUTTER_PX = 2;
const SIDENAV_CHILD_PREVIEW_GUTTER_PX = 4;
const SIDENAV_WORKSPACE_MENU_GUTTER_PX = 4;
const SIDENAV_HOVER_MENU_TOP_OFFSET_PX = -4;
// The sidebar flyout title is a sm title row (24px). Shift title-bearing
// menus up by that amount so the first selectable row keeps its original
// alignment with the parent sidenav row.
const SIDENAV_HOVER_MENU_TITLE_ROW_OFFSET_PX = 24;
const SIDENAV_HOVER_MENU_CLOSE_DELAY_MS = 100;
const SIDENAV_CHILD_PREVIEW_CLOSE_DELAY_MS = 80;
const SIDENAV_WORKSPACE_MENU_CLOSE_DELAY_MS = 100;
const SIDENAV_TOGGLE_TOOLTIP_GUTTER_PX = 4;

const renderSideNavIcon = (icon: SideNavV2Icon | undefined, className: string) => {
  if (!icon) return null;

  return <Icon name={icon} size={"md"} aria-hidden={true} className={className} />;
};

const SideNavWorkspaceAvatarV2Impl: React.FC<{
  label: string;
  icon?: SideNavV2Icon;
  themeMode?: SideNavV2Theme;
  decorative?: boolean;
  className?: string;
}> = ({ label, icon, themeMode, decorative = false, className }) => {
  if (!icon) {
    return (
      <span
        role={decorative ? undefined : "img"}
        aria-label={decorative ? undefined : label}
        aria-hidden={decorative ? true : undefined}
        className={cx(
          "block h-2.5 w-2.5 shrink-0 opacity-100",
          themeMode == null
            ? "!bg-black dark:!bg-white"
            : themeMode === "dark"
              ? "!bg-white"
              : "!bg-black",
          className,
        )}
      />
    );
  }

  const isDarkMode = themeMode !== "light";

  return (
    <span
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative ? true : undefined}
      className={cx(
        "flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-cds-3",
        themeMode == null
          ? [
              "bg-cds-surface-border !text-cds-ink-subtle [&_svg]:!text-cds-ink-subtle",
              "dark:bg-white/[0.08] dark:!text-zinc-500 dark:[&_svg]:!text-zinc-500",
            ]
          : isDarkMode
            ? "bg-white/[0.08] !text-zinc-500 [&_svg]:!text-zinc-500"
            : "bg-cds-surface-border !text-cds-ink-subtle [&_svg]:!text-cds-ink-subtle",
        className,
      )}
    >
      <Icon name={icon} size={"xs"} aria-hidden={true} className={"shrink-0"} />
    </span>
  );
};

export const SideNavWorkspaceAvatarV2 = React.memo(SideNavWorkspaceAvatarV2Impl);
SideNavWorkspaceAvatarV2.displayName = "SideNavWorkspaceAvatarV2";

interface SideNavHeaderV2Props {
  headerVariant: "workspace" | "back";
  workspaceHeaderRef: React.Ref<HTMLDivElement>;
  workspaceLabel: string;
  workspaceIcon?: SideNavV2Icon;
  themeMode?: SideNavV2Theme;
  isDarkMode: boolean;
  backLabel: string;
  backHref?: LinkProps["href"];
  onBackToApp?: () => void;
  hasWorkspaceMenu: boolean;
  workspaceMenuOpen: boolean;
  onWorkspaceSelect?: () => void;
  onToggleWorkspaceMenu: () => void;
  onWorkspaceHeaderMouseEnter: () => void;
  onWorkspaceHeaderMouseLeave: () => void;
  showToggleButton: boolean;
  toggleButtonRef: React.Ref<HTMLButtonElement>;
  toggleButtonIcon: SideNavV2Icon;
  toggleButtonLabel: string;
  toggleTooltipId: string;
  toggleTooltipVisible: boolean;
  onShowToggleTooltip: () => void;
  onHideToggleTooltip: () => void;
  onToggleCollapse?: () => void;
}

const SideNavHeaderV2Impl: React.FC<SideNavHeaderV2Props> = ({
  headerVariant,
  workspaceHeaderRef,
  workspaceLabel,
  workspaceIcon,
  themeMode,
  isDarkMode,
  backLabel,
  backHref,
  onBackToApp,
  hasWorkspaceMenu,
  workspaceMenuOpen,
  onWorkspaceSelect,
  onToggleWorkspaceMenu,
  onWorkspaceHeaderMouseEnter,
  onWorkspaceHeaderMouseLeave,
  showToggleButton,
  toggleButtonRef,
  toggleButtonIcon,
  toggleButtonLabel,
  toggleTooltipId,
  toggleTooltipVisible,
  onShowToggleTooltip,
  onHideToggleTooltip,
  onToggleCollapse,
}) => {
  return (
    <div
      ref={workspaceHeaderRef}
      className={cx(
        "relative z-10 flex h-7 shrink-0 items-center justify-between gap-cds-8 pl-cds-8 pr-0",
      )}
      onMouseEnter={onWorkspaceHeaderMouseEnter}
      onMouseLeave={onWorkspaceHeaderMouseLeave}
    >
      {headerVariant === "back" ? (
        backHref != null ? (
          <ChalkLink
            unstyled
            href={backHref}
            className={cx(
              "group inline-flex min-w-0 max-w-full items-center gap-cds-4 rounded-cds-6 text-left",
              themeMode == null
                ? "text-cds-ink-subtle hover:!text-cds-ink-secondary dark:!text-zinc-500 dark:hover:!text-zinc-400"
                : isDarkMode
                  ? "!text-zinc-500 hover:!text-zinc-400"
                  : "text-cds-ink-subtle hover:!text-cds-ink-secondary",
              "focus-visible:outline-none focus-visible:!shadow-cds-focus",
            )}
            onClick={onBackToApp}
          >
            <Icon
              name={"chevronLeft"}
              size={"sm"}
              aria-hidden={true}
              className={
                "shrink-0 text-current transition-[color,transform] duration-200 ease-out " +
                "group-hover:-translate-x-px"
              }
            />
            <span
              className={
                "truncate text-cds-sm font-medium text-current transition-colors duration-75"
              }
            >
              {backLabel}
            </span>
          </ChalkLink>
        ) : (
          <button
            type={"button"}
            className={cx(
              "group inline-flex min-w-0 max-w-full items-center gap-cds-4 rounded-cds-6 text-left",
              themeMode == null
                ? "text-cds-ink-subtle hover:!text-cds-ink-secondary dark:!text-zinc-500 dark:hover:!text-zinc-400"
                : isDarkMode
                  ? "!text-zinc-500 hover:!text-zinc-400"
                  : "text-cds-ink-subtle hover:!text-cds-ink-secondary",
              "focus-visible:outline-none focus-visible:!shadow-cds-focus",
            )}
            onClick={onBackToApp}
          >
            <Icon
              name={"chevronLeft"}
              size={"sm"}
              aria-hidden={true}
              className={
                "shrink-0 text-current transition-[color,transform] duration-200 ease-out " +
                "group-hover:-translate-x-px"
              }
            />
            <span
              className={
                "truncate text-cds-sm font-medium text-current transition-colors duration-75"
              }
            >
              {backLabel}
            </span>
          </button>
        )
      ) : (
        <button
          type={"button"}
          className={cx(
            "group inline-flex min-w-0 max-w-full shrink items-center gap-cds-8 rounded-cds-6 pl-cds-2 pr-cds-4 text-left",
            "dark:text-zinc-50 dark:hover:text-white",
            "text-cds-ink hover:text-cds-ink-strong",
            "focus-visible:outline-none focus-visible:!shadow-cds-focus",
          )}
          aria-haspopup={hasWorkspaceMenu ? "menu" : undefined}
          aria-expanded={hasWorkspaceMenu ? workspaceMenuOpen : undefined}
          onClick={() => {
            if (hasWorkspaceMenu) {
              onToggleWorkspaceMenu();
              return;
            }

            onWorkspaceSelect?.();
          }}
        >
          <SideNavWorkspaceAvatarV2
            label={workspaceLabel}
            icon={workspaceIcon}
            themeMode={themeMode}
            decorative
          />
          <span className={"flex min-w-0 items-center gap-cds-4"}>
            <span className={"truncate text-cds-sm font-medium"}>{workspaceLabel}</span>
            <Icon
              name={"chevronDown"}
              size={"sm"}
              aria-hidden={true}
              className={cx(
                "shrink-0 transition-[transform] duration-200 ease-out will-change-transform",
                "group-hover:translate-y-px",
                workspaceMenuOpen && "translate-y-px",
              )}
            />
          </span>
        </button>
      )}
      {showToggleButton && (
        <div className={"shrink-0"}>
          <IconButtonV2
            ref={toggleButtonRef}
            size={"sm"}
            iconSize={"md"}
            variant={"tertiary"}
            icon={toggleButtonIcon}
            aria-label={toggleButtonLabel}
            aria-describedby={toggleTooltipVisible ? toggleTooltipId : undefined}
            className={"shrink-0"}
            onBlur={onHideToggleTooltip}
            onFocus={onShowToggleTooltip}
            onMouseEnter={onShowToggleTooltip}
            onMouseLeave={onHideToggleTooltip}
            onClick={onToggleCollapse}
          />
        </div>
      )}
    </div>
  );
};

const SideNavHeaderV2 = React.memo(SideNavHeaderV2Impl);
SideNavHeaderV2.displayName = "SideNavHeaderV2";

const renderSideNavCountBadge = (count: string | number | undefined, selected: boolean) => {
  if (count == null) return null;

  return (
    <CountBadgeV2 aria-hidden={true} emphasis={selected ? "default" : "unhovered"}>
      {String(count)}
    </CountBadgeV2>
  );
};

const isModifiedSideNavClick = (event: React.MouseEvent) =>
  event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || event.button !== 0;

const normalizeSideNavLocation = (location: string | undefined) => {
  if (location == null || location.length === 0) return undefined;

  const suffixStart = location.search(/[?#]/);
  const pathname = suffixStart === -1 ? location : location.slice(0, suffixStart);
  const suffix = suffixStart === -1 ? "" : location.slice(suffixStart);
  const normalizedPathname = pathname.replace(/\/+$/, "") || "/";
  return `${normalizedPathname}${suffix}`;
};

const normalizeSideNavPath = (path: string | undefined) => {
  return normalizeSideNavLocation(path)?.split(/[?#]/)[0];
};

const getSideNavHrefPath = (href: LinkProps["href"] | undefined) => {
  if (typeof href === "string") return normalizeSideNavPath(href);
  return normalizeSideNavPath(href?.pathname ?? undefined);
};

const selectedSideNavItemNavigates = (
  currentPath: string | undefined,
  href: LinkProps["href"] | undefined,
) => {
  const normalizedCurrentLocation = normalizeSideNavLocation(currentPath);
  if (normalizedCurrentLocation == null) return false;

  if (typeof href === "string") {
    const normalizedHrefLocation = normalizeSideNavLocation(href);
    return normalizedHrefLocation != null && normalizedCurrentLocation !== normalizedHrefLocation;
  }

  const normalizedCurrentPath = normalizeSideNavPath(currentPath);
  const normalizedHrefPath = getSideNavHrefPath(href);
  if (normalizedCurrentPath == null || normalizedHrefPath == null) return false;
  return normalizedCurrentPath !== normalizedHrefPath;
};

interface SideNavHitTargetProps {
  "data-testid"?: string;
  label: string;
  href?: LinkProps["href"];
  shouldOpenInNewTab?: boolean;
  interactive: boolean;
  deprecated?: boolean;
  selected: boolean;
  allowPointerCursorWhenSelected?: boolean;
  suppressClickWhenSelected?: boolean;
  current?: boolean;
  tabIndex?: number;
  ariaHasPopup?: boolean;
  ariaExpanded?: boolean;
  onFocusElement?: (element: HTMLElement) => void;
  onBlur?: () => void;
  onSelect: () => void;
}

const SideNavHitTarget: React.FC<SideNavHitTargetProps> = ({
  "data-testid": dataTestId,
  label,
  href,
  shouldOpenInNewTab,
  interactive,
  deprecated = false,
  selected,
  allowPointerCursorWhenSelected = false,
  suppressClickWhenSelected = false,
  current,
  tabIndex,
  ariaHasPopup,
  ariaExpanded,
  onFocusElement,
  onBlur,
  onSelect,
}) => {
  const actionable = interactive && !deprecated;
  const className = cx(
    SIDENAV_ITEM_HIT_TARGET_CLASS,
    deprecated
      ? "cursor-not-allowed"
      : interactive && (!selected || allowPointerCursorWhenSelected)
        ? "cursor-pointer"
        : "cursor-default",
    "focus-visible:outline-none focus-visible:!shadow-cds-focus",
  );
  const handleFocus: React.FocusEventHandler<HTMLElement> = (event) => {
    if (!interactive) return;
    onFocusElement?.(event.currentTarget);
  };
  const handleBlur = () => onBlur?.();

  if (href != null && actionable) {
    return (
      <ChalkLink
        unstyled
        data-testid={dataTestId}
        href={href}
        openInNewTab={shouldOpenInNewTab}
        aria-label={label}
        aria-current={current ? "page" : undefined}
        aria-haspopup={ariaHasPopup ? "menu" : undefined}
        aria-expanded={ariaHasPopup ? ariaExpanded : undefined}
        tabIndex={tabIndex}
        className={className}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={(event) => {
          if (suppressClickWhenSelected && selected) {
            event.preventDefault();
            return;
          }
          if (isModifiedSideNavClick(event)) return;
          onSelect();
        }}
      />
    );
  }

  return (
    <button
      data-testid={dataTestId}
      type={"button"}
      aria-label={label}
      aria-current={current ? "page" : undefined}
      aria-haspopup={ariaHasPopup ? "menu" : undefined}
      aria-expanded={ariaHasPopup ? ariaExpanded : undefined}
      aria-disabled={deprecated || undefined}
      disabled={!interactive || deprecated}
      tabIndex={tabIndex}
      className={className}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={() => {
        if (suppressClickWhenSelected && selected) return;
        if (!actionable) return;
        onSelect();
      }}
    />
  );
};

const getSideNavItemChildren = <T extends string>(item: SideNavV2Item<T>) => item.children ?? [];
// Deprecated destinations remain hoverable for their visual treatment and tooltip, but their
// hit target is aria-disabled and does not navigate.
const renderSideNavItemLabel = <T extends string>(item: SideNavV2Item<T>): React.ReactNode =>
  item.deprecationNotice != null ? (
    <span className={"line-through opacity-60"}>{item.label}</span>
  ) : (
    item.label
  );
const getSideNavItemDescription = <T extends string>(item: SideNavV2Item<T>) =>
  item.description ?? `Open ${item.label} to view and manage this page.`;
const getSideNavItemChildMenuSections = <T extends string>(item: SideNavV2Item<T>) => {
  return getSideNavItemChildren(item).reduce<
    { id: string; label?: string; items: SideNavV2Item<T>[] }[]
  >((sections, child) => {
    const label = child.menuGroupLabel;
    const lastSection = sections[sections.length - 1];

    if (lastSection != null && lastSection.label === label) {
      lastSection.items.push(child);
      return sections;
    }

    sections.push({
      id: `${label ?? "ungrouped"}-${sections.length}`,
      label,
      items: [child],
    });
    return sections;
  }, []);
};
const getSideNavHoverMenuTitle = <T extends string>(
  item: SideNavV2Item<T>,
  sections: readonly { label?: string }[],
) => (sections.some((section) => section.label != null) ? null : item.label);

export const flattenSideNavItems = <T extends string>(
  items: readonly SideNavV2Item<T>[],
): SideNavV2Item<T>[] => {
  return items.flatMap((item) => [item, ...flattenSideNavItems(getSideNavItemChildren(item))]);
};

export const flattenSideNavSections = <T extends string>(
  sections: readonly SideNavV2Section<T>[],
): SideNavV2Item<T>[] => {
  return sections.flatMap((section) => flattenSideNavItems(section.items));
};

export const sideNavItemContainsId = <T extends string>(
  item: SideNavV2Item<T>,
  itemId: T | undefined,
): boolean => {
  if (itemId == null) return false;

  return getSideNavItemChildren(item).some(
    (child) =>
      child.deprecationNotice == null &&
      (child.id === itemId || sideNavItemContainsId(child, itemId)),
  );
};

interface SideNavItemRowProps<T extends string> {
  item: SideNavV2Item<T>;
  /** A grip at the row's right end, past its "…" button, that drags the row (`onArrangeRows`). */
  dragHandle?: {
    /** The row is lifted, so the grip and the room for it stay without the hover. */
    lifted: boolean;
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
  };
  activeItemId?: T;
  hoverMenuOpen: boolean;
  hoverMenusDisabled: boolean;
  sectionHidden: boolean;
  currentPath?: string;
  onOpenHoverMenu: (item: SideNavV2Item<T>, element: HTMLElement) => void;
  onScheduleHoverMenuClose: () => void;
  onOpenChildPreview: (item: SideNavV2Item<T>, element: HTMLElement) => void;
  onScheduleChildPreviewClose: () => void;
  onCloseChildPreview: () => void;
  onCloseHoverMenu: () => void;
  onBeforeItemSelect?: (item: SideNavV2Item<T>) => void;
  onItemSelect?: (item: SideNavV2Item<T>) => void;
  onItemActionsMenuOpenChange?: (open: boolean) => void;
}

const SIDENAV_ITEM_ACTIONS_MENU_WIDTH_PX = 160;
const SIDENAV_ITEM_ACTIONS_MENU_GUTTER_PX = 4;
// Pull a row's label and count badge clear of its "…" button while the button shows: always while
// its menu is open, otherwise on hover or focus. 28px is the 24px button, its 2px inset from the
// row's edge, and the same 2px again as the gap before the badge. Written out in full so Tailwind
// can see each.
const SIDENAV_ITEM_ACTIONS_LABEL_PADDING_CLASS = "!pr-[28px]";
const SIDENAV_ITEM_ACTIONS_LABEL_HOVER_PADDING_CLASS =
  "group-hover/sidenav-hit:!pr-[28px] group-focus-within/sidenav-hit:!pr-[28px]";
// With a drag grip, 16px wide and 2px in from the row's right end, the "…" button's box overlaps
// the grip's by 2px, which leaves its glyph 5px from the grip's; the label and badge pull in 42px
// while both show, and a row with the grip alone pulls in 20px.
const SIDENAV_ITEM_ACTIONS_BESIDE_HANDLE_CLASS = "!right-[16px]";
const SIDENAV_ITEM_ACTIONS_AND_HANDLE_LABEL_PADDING_CLASS = "!pr-[42px]";
const SIDENAV_ITEM_ACTIONS_AND_HANDLE_LABEL_HOVER_PADDING_CLASS =
  "group-hover/sidenav-hit:!pr-[42px] group-focus-within/sidenav-hit:!pr-[42px]";
const SIDENAV_ITEM_HANDLE_LABEL_PADDING_CLASS = "!pr-[20px]";
const SIDENAV_ITEM_HANDLE_LABEL_HOVER_PADDING_CLASS =
  "group-hover/sidenav-hit:!pr-[20px] group-focus-within/sidenav-hit:!pr-[20px]";
// Eases the badge aside as the button appears, as the SQL explorer's tree rows do. It extends
// MenuItemV2's color transition rather than replacing it, so text and icon colors keep their 75ms.
const SIDENAV_ITEM_ACTIONS_PADDING_TRANSITION_CLASS =
  "![transition-property:color,border-color,text-decoration-color,fill,stroke,padding-right] " +
  "![transition-duration:75ms,75ms,75ms,75ms,75ms,100ms]";

/** A row's "…" button and the menu of its actions. */
const SideNavItemActions: React.FC<{
  label: string;
  actions: readonly SideNavV2ItemAction[];
  /** The row has a drag grip at its right end, so the button sits beside it. */
  besideDragHandle?: boolean;
  /**
   * A section title's menu sits beside the title's grip (4px in, 16px wide), spaced as a row's is,
   * and shows while the title is hovered, level with its text.
   */
  placement?: "row" | "section-title";
  onOpenChange: (open: boolean) => void;
}> = ({ label, actions, besideDragHandle = false, placement = "row", onOpenChange }) => {
  const overlayPortalRoot = useOverlayPortalRoot();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ left: number; top: number } | null>(null);
  const open = menuPosition != null;
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const closeTimerRef = useRef<number | null>(null);
  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current == null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);
  const closeMenu = useCallback(() => {
    cancelScheduledClose();
    setMenuPosition(null);
  }, [cancelScheduledClose]);
  // Leaving the menu, or the button without moving onto the menu, dismisses it. The delay
  // covers the gap between the button and the menu.
  const scheduleClose = useCallback(() => {
    cancelScheduledClose();
    closeTimerRef.current = window.setTimeout(closeMenu, SIDENAV_HOVER_MENU_CLOSE_DELAY_MS);
  }, [cancelScheduledClose, closeMenu]);
  useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

  useEffect(() => {
    onOpenChangeRef.current(open);
  }, [open]);
  // A row can unmount with its menu open, such as when a hover flyout swaps categories.
  useEffect(() => () => onOpenChangeRef.current(false), []);
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      closeMenu();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeMenu, open]);

  return (
    <>
      <IconButtonV2
        ref={buttonRef}
        size={"xs"}
        iconSize={"sm"}
        variant={"tertiary"}
        icon={"more"}
        aria-label={`${label} actions`}
        aria-haspopup={"menu"}
        aria-expanded={open}
        // Above the row's hit target, so pressing it never follows the row's link.
        className={cx(
          placement === "section-title"
            ? "absolute right-[18px] top-[4px] z-30 !h-6 !w-6"
            : "absolute right-cds-2 top-1/2 z-30 !h-6 !w-6 -translate-y-1/2",
          besideDragHandle && SIDENAV_ITEM_ACTIONS_BESIDE_HANDLE_CLASS,
          open
            ? "opacity-100"
            : placement === "section-title"
              ? "opacity-0 focus-visible:opacity-100 group-hover/section-heading:opacity-100"
              : "opacity-0 focus-visible:opacity-100 group-hover/sidenav-hit:opacity-100",
        )}
        // A mouse press must not focus the button: once its menu closes on mouse-out, the hidden
        // button would keep the row's focus-within styles, holding the badge aside and the row lit.
        onMouseDown={(event) => event.preventDefault()}
        onMouseEnter={cancelScheduledClose}
        onMouseLeave={() => {
          if (open) scheduleClose();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          const rect = buttonRef.current?.getBoundingClientRect();
          if (rect == null) return;
          setMenuPosition((previous) =>
            previous == null
              ? { left: rect.left, top: rect.bottom + SIDENAV_ITEM_ACTIONS_MENU_GUTTER_PX }
              : null,
          );
        }}
      />
      {menuPosition != null &&
        typeof document !== "undefined" &&
        createPortal(
          <ViewportAwareFixedMenuV2
            ref={menuRef}
            className={"design-system-typography text-cds-md"}
            // Rows live in hover flyouts too, so the menu stacks above the flyout layer.
            style={{ zIndex: ZIndex.TOOLTIP }}
            left={menuPosition.left}
            top={menuPosition.top}
            width={SIDENAV_ITEM_ACTIONS_MENU_WIDTH_PX}
            onMouseEnter={cancelScheduledClose}
            onMouseLeave={scheduleClose}
          >
            <MenuContainerV2 className={"w-full"}>
              <MenuSectionV2>
                {actions.map((action) => (
                  <MenuItemV2
                    key={action.id}
                    size={"sm"}
                    label={action.label}
                    leftIcon={action.icon}
                    onClick={() => {
                      closeMenu();
                      action.onSelect();
                    }}
                  />
                ))}
              </MenuSectionV2>
            </MenuContainerV2>
          </ViewportAwareFixedMenuV2>,
          overlayPortalRoot ?? document.body,
        )}
    </>
  );
};

const SideNavItemRowImpl = <T extends string>({
  item,
  dragHandle,
  activeItemId,
  hoverMenuOpen,
  hoverMenusDisabled,
  sectionHidden,
  currentPath,
  onOpenHoverMenu,
  onScheduleHoverMenuClose,
  onOpenChildPreview,
  onScheduleChildPreviewClose,
  onCloseChildPreview,
  onCloseHoverMenu,
  onBeforeItemSelect,
  onItemSelect,
  onItemActionsMenuOpenChange,
}: SideNavItemRowProps<T>) => {
  const redSurfacesSuppressed = useRedSurfacesSuppressed();
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const hasActions = (item.actions?.length ?? 0) > 0 && !sectionHidden;
  const hasDragHandle = dragHandle != null && !sectionHidden;
  // The room the "…" button and the drag grip take at the row's right while they show: held while
  // the actions menu is open or the row is lifted, neither of which keeps the hover.
  const rightRoom = hasActions
    ? hasDragHandle
      ? {
          held: SIDENAV_ITEM_ACTIONS_AND_HANDLE_LABEL_PADDING_CLASS,
          hover: SIDENAV_ITEM_ACTIONS_AND_HANDLE_LABEL_HOVER_PADDING_CLASS,
        }
      : {
          held: SIDENAV_ITEM_ACTIONS_LABEL_PADDING_CLASS,
          hover: SIDENAV_ITEM_ACTIONS_LABEL_HOVER_PADDING_CLASS,
        }
    : hasDragHandle
      ? {
          held: SIDENAV_ITEM_HANDLE_LABEL_PADDING_CLASS,
          hover: SIDENAV_ITEM_HANDLE_LABEL_HOVER_PADDING_CLASS,
        }
      : undefined;
  const handleActionsMenuOpenChange = useCallback(
    (open: boolean) => {
      setActionsMenuOpen(open);
      onItemActionsMenuOpenChange?.(open);
    },
    [onItemActionsMenuOpenChange],
  );
  const selectable = item.deprecationNotice == null;
  const active = selectable && item.id === activeItemId;
  const childActive = selectable && sideNavItemContainsId(item, activeItemId);
  const menuOnlyChildren = item.childrenDisplay === "menu";
  const visuallyActive = active || childActive;
  const expanded = !menuOnlyChildren && visuallyActive;
  const children = getSideNavItemChildren(item);
  const selectedItemNavigates =
    item.allowNavigationWhenSelected !== false &&
    children.length === 0 &&
    selectedSideNavItemNavigates(currentPath, item.href);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const showsChildMenuAffordance = children.length > 0 && !(menuOnlyChildren && active);
  const hoverMenuEligible =
    !hoverMenusDisabled &&
    !sectionHidden &&
    showsChildMenuAffordance &&
    !item.disabled &&
    item.deprecationNotice == null &&
    (menuOnlyChildren || !expanded);
  const leafCountBadge = children.length === 0 ? renderSideNavCountBadge(item.count, active) : null;
  const itemInteractive = !sectionHidden && !item.disabled;
  const handleItemSelect = () => {
    onCloseHoverMenu();
    onBeforeItemSelect?.(item);
    item.onSelect?.();
    onItemSelect?.(item);
  };

  const itemRow = (
    <div className={"group/sidenav-hit relative"}>
      <MenuItemV2
        aria-hidden={true}
        size={"md"}
        label={renderSideNavItemLabel(item)}
        leftIcon={item.icon}
        rightIcon={showsChildMenuAffordance ? "chevronRight" : undefined}
        rightAccessory={leafCountBadge}
        rightIconClassName={cx(
          showsChildMenuAffordance &&
            (expanded ? SIDENAV_DISCLOSURE_ICON_OPEN_CLASS : SIDENAV_DISCLOSURE_ICON_CLASS),
        )}
        active={visuallyActive}
        tabIndex={-1}
        className={cx(
          "pointer-events-none",
          item.visualTone === "subtle" ? SIDENAV_ITEM_SUBTLE_CLASS : SIDENAV_ITEM_CLASS,
          // The held padding replaces the base padding rather than stacking on it: the two tie on
          // specificity, and `!pr-cds-6` sorts after `!pr-cds-24` in the stylesheet.
          rightRoom != null && ((hasActions && actionsMenuOpen) || dragHandle?.lifted === true)
            ? rightRoom.held
            : showsChildMenuAffordance || leafCountBadge
              ? SIDENAV_RIGHT_AFFORDANCE_PADDING_CLASS
              : SIDENAV_DEFAULT_RIGHT_PADDING_CLASS,
          hoverMenuOpen && SIDENAV_ITEM_HOVER_MENU_OPEN_CLASS,
          !visuallyActive &&
            (item.visualTone === "subtle"
              ? SIDENAV_ITEM_SUBTLE_GROUP_HOVER_CLASS
              : SIDENAV_ITEM_GROUP_HOVER_CLASS),
          active && SIDENAV_ITEM_ACTIVE_CLASS,
          !active && childActive && SIDENAV_ITEM_DESCENDANT_ACTIVE_CLASS,
          getSideNavItemVisualTreatmentClass(item, redSurfacesSuppressed),
          visuallyActive && getSideNavItemVisualTreatmentStableClass(item, redSurfacesSuppressed),
          hoverMenuOpen &&
            !visuallyActive &&
            getSideNavItemVisualTreatmentHoverClass(item, redSurfacesSuppressed),
          hasActions && actionsMenuOpen && SIDENAV_ITEM_HOVER_MENU_OPEN_CLASS,
          rightRoom != null && !(hasActions && actionsMenuOpen) && rightRoom.hover,
          rightRoom != null && SIDENAV_ITEM_ACTIONS_PADDING_TRANSITION_CLASS,
        )}
      />
      <SideNavHitTarget
        data-testid={`sidebar-${item.id}`}
        label={item.label}
        href={item.href}
        shouldOpenInNewTab={item.shouldOpenInNewTab}
        interactive={itemInteractive}
        deprecated={item.deprecationNotice != null}
        selected={visuallyActive}
        // Leaf rows can navigate back to their landing page from a descendant route. Parent
        // groups stay non-actionable while expanded, even when their href differs from the
        // active child's route.
        suppressClickWhenSelected={visuallyActive && !selectedItemNavigates}
        allowPointerCursorWhenSelected={selectedItemNavigates}
        current={active}
        ariaHasPopup={hoverMenuEligible}
        ariaExpanded={hoverMenuOpen}
        tabIndex={sectionHidden ? -1 : undefined}
        onFocusElement={(element) => {
          if (hoverMenuEligible) onOpenHoverMenu(item, rowRef.current ?? element);
        }}
        onBlur={() => {
          if (hoverMenuEligible) onScheduleHoverMenuClose();
        }}
        onSelect={handleItemSelect}
      />
      {hasActions && item.actions != null && (
        <SideNavItemActions
          label={item.label}
          actions={item.actions}
          besideDragHandle={hasDragHandle}
          onOpenChange={handleActionsMenuOpenChange}
        />
      )}
      {hasDragHandle && (
        <SideNavDragHandleTooltip lifted={dragHandle.lifted}>
          {/* Above the row's hit target, so pressing it never follows the row's link. The press
              starts no text selection, and the hook turns it into a drag once it travels. */}
          <span
            aria-hidden={true}
            data-sidenav-row-drag-handle={""}
            className={cx(
              "absolute right-cds-2 top-1/2 z-30 flex h-6 w-4 -translate-y-1/2 cursor-grab",
              "items-center justify-center transition-opacity duration-75",
              "text-cds-ink-subtle hover:text-cds-ink dark:text-zinc-500 dark:hover:text-zinc-200",
              dragHandle.lifted
                ? "opacity-100"
                : "opacity-0 group-hover/sidenav-hit:opacity-100 group-focus-within/sidenav-hit:opacity-100",
            )}
            onPointerDown={(event) => {
              event.preventDefault();
              dragHandle.onPointerDown(event);
            }}
          >
            <Icon name={"gripVertical"} size={"xs"} aria-hidden={true} />
          </span>
        </SideNavDragHandleTooltip>
      )}
    </div>
  );

  return (
    <div
      ref={rowRef}
      role={"listitem"}
      className={cx("flex flex-col", expanded && SIDENAV_ITEM_GAP_CLASS)}
      onMouseEnter={(event) => {
        if (!hoverMenuEligible) return;
        onOpenHoverMenu(item, event.currentTarget);
      }}
      onMouseLeave={() => {
        if (!hoverMenuEligible) return;
        onScheduleHoverMenuClose();
      }}
    >
      {item.deprecationNotice == null ? (
        item.renderHoverPreview?.(itemRow) ?? itemRow
      ) : (
        <TooltipV2 content={item.deprecationNotice} placement={"right"} persistOnHover={false}>
          {itemRow}
        </TooltipV2>
      )}
      {children.length > 0 && (
        <div
          aria-hidden={!expanded}
          data-sidenav-child-panel={""}
          className={cx(
            "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
            expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
          )}
        >
          <div className={"min-h-0 -mx-[5px] overflow-hidden px-[5px]"}>
            <div
              data-sidenav-child-preview-anchor={""}
              className={cx("relative flex flex-col", SIDENAV_ITEM_GAP_CLASS)}
            >
              <span
                aria-hidden={true}
                data-sidenav-child-connector={""}
                className={cx(
                  "pointer-events-none absolute bottom-0 top-0 z-10 w-px",
                  "left-[15px] -translate-x-1/2",
                  "dark:bg-white/[0.12] bg-cds-surface-border",
                )}
              />
              {children.map((child) => {
                const childSelected = child.deprecationNotice == null && child.id === activeItemId;
                const childInteractive = expanded && !sectionHidden && !child.disabled;
                const handleChildSelect = () => {
                  onCloseChildPreview();
                  onBeforeItemSelect?.(child);
                  child.onSelect?.();
                  onItemSelect?.(child);
                };

                const childRow = (
                  <div
                    key={child.id}
                    className={"group/sidenav-child-hit relative"}
                    onMouseEnter={(event) => {
                      if (!childInteractive || child.deprecationNotice != null) return;
                      onOpenChildPreview(child, event.currentTarget);
                    }}
                    onMouseLeave={() => {
                      onScheduleChildPreviewClose();
                    }}
                  >
                    {(childSelected || childInteractive) && (
                      <span
                        aria-hidden={true}
                        className={cx(
                          "pointer-events-none absolute inset-y-0 z-20 w-px transition-[color,opacity] duration-75",
                          "left-[15px] -translate-x-1/2",
                          childSelected
                            ? "opacity-100"
                            : "opacity-0 group-hover/sidenav-child-hit:opacity-100 group-focus-within/sidenav-child-hit:opacity-100",
                          "dark:bg-white/[0.24] bg-zinc-400",
                        )}
                      />
                    )}
                    <div className={"pl-cds-24"}>
                      <MenuItemV2
                        aria-hidden={true}
                        size={"sm"}
                        label={renderSideNavItemLabel(child)}
                        leftIcon={child.icon}
                        rightAccessory={renderSideNavCountBadge(child.count, childSelected)}
                        active={childSelected}
                        tabIndex={-1}
                        className={cx(
                          "pointer-events-none",
                          SIDENAV_CHILD_ITEM_CLASS,
                          child.count != null
                            ? SIDENAV_RIGHT_AFFORDANCE_PADDING_CLASS
                            : SIDENAV_DEFAULT_RIGHT_PADDING_CLASS,
                          // Demo mode drops the danger branch too: the row
                          // takes the standard hover/active classes like any
                          // normal child.
                          child.visualTone === "danger" && !redSurfacesSuppressed
                            ? childSelected
                              ? SIDENAV_CHILD_ITEM_DANGER_ACTIVE_CLASS
                              : SIDENAV_CHILD_ITEM_DANGER_CLASS
                            : [
                                !childSelected && SIDENAV_CHILD_ITEM_GROUP_HOVER_CLASS,
                                childSelected && SIDENAV_CHILD_ITEM_ACTIVE_CLASS,
                                // Admin surface: the visual-treatment refactor covered the
                                // danger tone here but dropped the admin case inline child
                                // rows previously had.
                                getSideNavItemVisualTreatmentClass(child, redSurfacesSuppressed),
                              ],
                        )}
                      />
                    </div>
                    <SideNavHitTarget
                      label={child.label}
                      href={child.href}
                      shouldOpenInNewTab={child.shouldOpenInNewTab}
                      interactive={childInteractive}
                      deprecated={child.deprecationNotice != null}
                      selected={childSelected}
                      allowPointerCursorWhenSelected={
                        childSelected && selectedSideNavItemNavigates(currentPath, child.href)
                      }
                      current={childSelected}
                      tabIndex={expanded && !sectionHidden ? undefined : -1}
                      onFocusElement={(element) => {
                        if (child.deprecationNotice != null) return;
                        onOpenChildPreview(child, element);
                      }}
                      onBlur={onScheduleChildPreviewClose}
                      onSelect={handleChildSelect}
                    />
                  </div>
                );

                if (child.deprecationNotice == null) {
                  return childRow;
                }

                return (
                  <TooltipV2
                    key={child.id}
                    content={child.deprecationNotice}
                    placement={"right"}
                    persistOnHover={false}
                  >
                    {childRow}
                  </TooltipV2>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SideNavItemRow = React.memo(SideNavItemRowImpl) as typeof SideNavItemRowImpl & {
  displayName?: string;
};
SideNavItemRow.displayName = "SideNavItemRow";

const SIDENAV_WORKSPACE_SUBMENU_WIDTH_CLASS = "w-[220px]";

// One workspace-menu row, recursing into `children` as a MenuItemV2 hover
// submenu. Leaf rows run their command and dismiss the whole menu; rows with
// children exist only to host the submenu, so they must not dismiss it.
const SideNavWorkspaceMenuRow: React.FC<{
  item: SideNavV2WorkspaceMenuItem;
  redSurfacesSuppressed: boolean;
  onCloseMenu: () => void;
}> = ({ item, redSurfacesSuppressed, onCloseMenu }) => {
  const children = item.children ?? [];
  const hasChildren = children.length > 0;

  return (
    <MenuItemV2
      size={"sm"}
      label={item.label}
      href={hasChildren ? undefined : item.href}
      leftIcon={item.icon}
      rightAccessory={renderSideNavCountBadge(item.count, false)}
      className={cx(
        item.count != null && SIDENAV_RIGHT_AFFORDANCE_PADDING_CLASS,
        getSideNavItemVisualTreatmentClass(item, redSurfacesSuppressed),
      )}
      submenu={
        hasChildren ? (
          <MenuContainerV2 className={SIDENAV_WORKSPACE_SUBMENU_WIDTH_CLASS}>
            <MenuSectionV2>
              {children.map((child) => (
                <SideNavWorkspaceMenuRow
                  key={child.id}
                  item={child}
                  redSurfacesSuppressed={redSurfacesSuppressed}
                  onCloseMenu={onCloseMenu}
                />
              ))}
            </MenuSectionV2>
          </MenuContainerV2>
        ) : undefined
      }
      onClick={() => {
        if (hasChildren) return;

        item.onSelect?.();
        onCloseMenu();
      }}
    />
  );
};

export const SideNavV2 = <T extends string = string>({
  workspaceLabel,
  workspaceIcon,
  headerVariant = "workspace",
  showHeader = true,
  backLabel = "Back to app",
  sections,
  activeItemId,
  onBeforeItemSelect,
  onItemSelect,
  onWorkspaceSelect,
  workspaceMenuItems,
  headerAccessory,
  footerAccessory,
  backHref,
  onBackToApp,
  onToggleCollapse,
  showToggleButton = true,
  toggleButtonIcon = "sidebar",
  toggleButtonLabel = "Collapse sidebar",
  toggleTooltipLabel = "Hide",
  toggleTooltipShortcut = "bracket",
  hoverMenuOpenDelayMs = 0,
  hoverMenusDisabled = false,
  showDetailPanes = true,
  scrollPositionKey,
  currentPath,
  themeMode,
  collapsedSectionIds: controlledCollapsedSectionIds,
  onSectionCollapsedChange,
  onReorderSections,
  onArrangeRows,
  rowDropTargets,
  sectionDropTargets,
  onReorderDragChange,
  onRenameSection,
  onCreateSection,
  onDeleteSection,
  onItemActionsMenuOpenChange,
  surface = "backing",
  className,
  "aria-label": ariaLabel = "Primary navigation",
  ...rest
}: SideNavV2Props<T>) => {
  const overlayPortalRoot = useOverlayPortalRoot();
  const redSurfacesSuppressed = useRedSurfacesSuppressed();
  const isDarkMode = themeMode === "dark";
  // Scopes the item palette above. An unset themeMode leaves both classes off so
  // the cascade falls through to <html>, which the blocking theme script has
  // already set correctly by first paint; naming a theme here would instead
  // require knowing it during SSR, when next-themes cannot yet report one.
  const themeClass =
    themeMode === "dark" ? "dark" : themeMode === "light" ? "theme-light" : undefined;
  // Written out per theme so Tailwind can see every class; see the item palette notes above.
  const surfaceClass =
    surface === "elevated"
      ? themeMode == null
        ? "bg-cds-surface-default dark:bg-cds-elevated-dark"
        : isDarkMode
          ? "bg-cds-elevated-dark"
          : "bg-cds-surface-default"
      : themeMode == null
        ? "bg-cds-surface-active dark:bg-cds-inset-dark"
        : isDarkMode
          ? "bg-cds-inset-dark"
          : "bg-cds-surface-active";
  const toggleTooltipId = useId();
  const sectionsId = useId();
  const [uncontrolledCollapsedSectionIds, setCollapsedSectionIds] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        sections
          .filter(
            (section) =>
              section.collapsible &&
              section.defaultCollapsed &&
              !sectionHoldsItem(section.items, activeItemId),
          )
          .map((section) => section.id),
      ),
  );
  // Selecting an item, or loading a link to one, must leave its own section open. Only the active
  // item triggers this, so a reader can still collapse the section that holds it.
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  useEffect(() => {
    const holder = sectionsRef.current.find((section) =>
      sectionHoldsItem(section.items, activeItemId),
    );
    if (holder == null) return;
    setCollapsedSectionIds((current) => {
      if (!current.has(holder.id)) return current;
      const next = new Set(current);
      next.delete(holder.id);
      return next;
    });
  }, [activeItemId]);
  const collapsedSectionIds = controlledCollapsedSectionIds ?? uncontrolledCollapsedSectionIds;
  const toggleSection = useCallback(
    (sectionId: string) => {
      const collapsing = !collapsedSectionIds.has(sectionId);
      if (controlledCollapsedSectionIds == null) {
        setCollapsedSectionIds((current) => {
          const next = new Set(current);
          if (collapsing) next.add(sectionId);
          else next.delete(sectionId);
          return next;
        });
      }
      onSectionCollapsedChange?.(sectionId, collapsing);
    },
    [collapsedSectionIds, controlledCollapsedSectionIds, onSectionCollapsedChange],
  );
  const navRef = useRef<HTMLElement | null>(null);
  const workspaceHeaderRef = useRef<HTMLDivElement | null>(null);
  const workspaceMenuRef = useRef<HTMLDivElement | null>(null);
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);
  const [hoverMenu, setHoverMenu] = useState<{
    item: SideNavV2Item<T>;
    sections: { id: string; label?: string; items: SideNavV2Item<T>[] }[];
    title: string | null;
    left: number;
    top: number;
    width: number;
  } | null>(null);
  const [toggleTooltipPosition, setToggleTooltipPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [childPreview, setChildPreview] = useState<{
    item: SideNavV2Item<T>;
    left: number;
    top: number;
    height: number;
    width: number;
  } | null>(null);
  const [workspaceMenuPosition, setWorkspaceMenuPosition] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);
  const hoverMenuOpenTimerRef = useRef<number | null>(null);
  const hoverMenuCloseTimerRef = useRef<number | null>(null);
  const childPreviewCloseTimerRef = useRef<number | null>(null);
  const workspaceMenuCloseTimerRef = useRef<number | null>(null);
  const workspaceMenuOpen = workspaceMenuPosition != null;
  const hasWorkspaceMenu = Boolean(workspaceMenuItems?.length);
  const hasWorkspaceSubmenu = Boolean(
    workspaceMenuItems?.some((item) => (item.children?.length ?? 0) > 0),
  );
  const scrollTop = usePersistentScrollTop(scrollPositionKey);
  const { rememberScrollPosition, scrollRef } =
    usePersistentScrollPosition<HTMLDivElement>(scrollPositionKey);
  const firstTitledSectionIndex = sections.findIndex((section) => Boolean(section.label));
  const fixedSectionCount =
    onReorderSections == null || firstTitledSectionIndex < 0
      ? sections.length
      : firstTitledSectionIndex;
  const movableSections = useMemo(
    () => sections.slice(fixedSectionCount),
    [fixedSectionCount, sections],
  );
  const sectionReorder = useDragReorder({
    items: movableSections,
    getId: getSideNavEntryId,
    externalTargets: sectionDropTargets,
    onReorder: (movedIds) =>
      onReorderSections?.([
        ...sections.slice(0, fixedSectionCount).map(getSideNavEntryId),
        ...movedIds,
      ]),
  });
  const renderedSections = [
    ...sections.slice(0, fixedSectionCount),
    ...sectionReorder.orderedItems,
  ];
  // With `onArrangeRows`, rows drag within and between the sections that do not fix them. The
  // whole row is the handle: a press that travels under 4px still clicks.
  const rowListSections =
    onArrangeRows == null
      ? []
      : renderedSections.filter((section) => section.fixedRows !== true && section.hidden !== true);
  const rowListIds = new Set(rowListSections.map((section) => section.id));
  const rowReorder = useDragReorderLists({
    lists: rowListSections.map((section) => ({
      id: section.id,
      items: section.items,
      acceptsDrops: !(section.collapsible === true && collapsedSectionIds.has(section.id)),
    })),
    getId: getSideNavEntryId,
    externalTargets: rowDropTargets,
    onReorder: (arrangement) =>
      onArrangeRows?.(arrangement.map(({ listId, ids }) => ({ sectionId: listId, itemIds: ids }))),
  });
  const reorderDragging = sectionReorder.draggingId != null || rowReorder.draggingId != null;
  const onReorderDragChangeRef = useRef(onReorderDragChange);
  onReorderDragChangeRef.current = onReorderDragChange;
  const reportedDraggingRef = useRef(false);
  useEffect(() => {
    if (reportedDraggingRef.current === reorderDragging) return;
    reportedDraggingRef.current = reorderDragging;
    onReorderDragChangeRef.current?.(reorderDragging);
  }, [reorderDragging]);
  // A nav unmounting mid-drag lets its host go.
  useEffect(
    () => () => {
      if (reportedDraggingRef.current) onReorderDragChangeRef.current?.(false);
    },
    [],
  );
  const [renamingSectionId, setRenamingSectionId] = useState<string | null>(null);
  // A dragged row can float past its own section's edge, which would otherwise clip it.
  const rowDragOverflowClass = rowReorder.draggingId != null && "overflow-visible";

  const clearHoverMenuCloseTimer = useCallback(() => {
    if (hoverMenuCloseTimerRef.current != null) {
      window.clearTimeout(hoverMenuCloseTimerRef.current);
      hoverMenuCloseTimerRef.current = null;
    }
  }, []);

  const clearHoverMenuOpenTimer = useCallback(() => {
    if (hoverMenuOpenTimerRef.current != null) {
      window.clearTimeout(hoverMenuOpenTimerRef.current);
      hoverMenuOpenTimerRef.current = null;
    }
  }, []);

  const clearChildPreviewCloseTimer = useCallback(() => {
    if (childPreviewCloseTimerRef.current != null) {
      window.clearTimeout(childPreviewCloseTimerRef.current);
      childPreviewCloseTimerRef.current = null;
    }
  }, []);

  const clearWorkspaceMenuCloseTimer = useCallback(() => {
    if (workspaceMenuCloseTimerRef.current != null) {
      window.clearTimeout(workspaceMenuCloseTimerRef.current);
      workspaceMenuCloseTimerRef.current = null;
    }
  }, []);

  const closeChildPreview = useCallback(() => {
    clearChildPreviewCloseTimer();
    setChildPreview(null);
  }, [clearChildPreviewCloseTimer]);

  const closeHoverMenu = useCallback(() => {
    clearHoverMenuOpenTimer();
    clearHoverMenuCloseTimer();
    setHoverMenu(null);
    closeChildPreview();
  }, [clearHoverMenuCloseTimer, clearHoverMenuOpenTimer, closeChildPreview]);

  useEffect(() => {
    if (!hoverMenusDisabled) return;

    closeHoverMenu();
  }, [closeHoverMenu, hoverMenusDisabled]);

  const scheduleHoverMenuClose = useCallback(() => {
    clearHoverMenuOpenTimer();
    clearHoverMenuCloseTimer();
    hoverMenuCloseTimerRef.current = window.setTimeout(() => {
      setHoverMenu(null);
      closeChildPreview();
    }, SIDENAV_HOVER_MENU_CLOSE_DELAY_MS);
  }, [clearHoverMenuCloseTimer, clearHoverMenuOpenTimer, closeChildPreview]);

  const scheduleChildPreviewClose = useCallback(() => {
    clearChildPreviewCloseTimer();
    childPreviewCloseTimerRef.current = window.setTimeout(() => {
      setChildPreview(null);
    }, SIDENAV_CHILD_PREVIEW_CLOSE_DELAY_MS);
  }, [clearChildPreviewCloseTimer]);

  const openHoverMenu = useCallback(
    (item: SideNavV2Item<T>, element: HTMLElement) => {
      if (hoverMenusDisabled) return;

      const children = getSideNavItemChildren(item);

      if (item.disabled || children.length === 0) return;

      const open = () => {
        if (!element.isConnected) return;

        const rect = element.getBoundingClientRect();
        const sideNavWidth = navRef.current?.getBoundingClientRect().width ?? 0;
        const sections = getSideNavItemChildMenuSections(item);
        const title = getSideNavHoverMenuTitle(item, sections);
        const titleOffset = title != null ? SIDENAV_HOVER_MENU_TITLE_ROW_OFFSET_PX : 0;

        setHoverMenu({
          item,
          sections,
          title,
          left: rect.right + SIDENAV_HOVER_MENU_GUTTER_PX,
          top: rect.top + SIDENAV_HOVER_MENU_TOP_OFFSET_PX - titleOffset,
          width: sideNavWidth > 0 ? sideNavWidth : rect.width,
        });
      };

      clearHoverMenuOpenTimer();
      clearHoverMenuCloseTimer();

      if (hoverMenuOpenDelayMs <= 0) {
        open();
        return;
      }

      hoverMenuOpenTimerRef.current = window.setTimeout(() => {
        hoverMenuOpenTimerRef.current = null;
        open();
      }, hoverMenuOpenDelayMs);
    },
    [clearHoverMenuCloseTimer, clearHoverMenuOpenTimer, hoverMenuOpenDelayMs, hoverMenusDisabled],
  );

  const openChildPreview = useCallback(
    (item: SideNavV2Item<T>, element: HTMLElement) => {
      if (!showDetailPanes || item.disabled) return;

      clearChildPreviewCloseTimer();

      const anchorElement =
        (element.closest("[data-sidenav-child-preview-anchor]") as HTMLElement | null) ?? element;
      const rect = anchorElement.getBoundingClientRect();
      const sideNavWidth = navRef.current?.getBoundingClientRect().width ?? 0;
      const nextPreview = {
        item,
        left: rect.right + SIDENAV_CHILD_PREVIEW_GUTTER_PX,
        top: rect.top,
        height: rect.height,
        width: sideNavWidth > 0 ? sideNavWidth : rect.width,
      };
      setChildPreview((previousPreview) => {
        if (
          previousPreview?.item === item &&
          previousPreview.left === nextPreview.left &&
          previousPreview.top === nextPreview.top &&
          previousPreview.height === nextPreview.height &&
          previousPreview.width === nextPreview.width
        ) {
          return previousPreview;
        }

        return nextPreview;
      });
    },
    [clearChildPreviewCloseTimer, showDetailPanes],
  );

  const showToggleTooltip = useCallback(() => {
    const rect = toggleButtonRef.current?.getBoundingClientRect();
    if (!rect) return;

    setToggleTooltipPosition({
      left: rect.left + rect.width / 2,
      top: rect.bottom + SIDENAV_TOGGLE_TOOLTIP_GUTTER_PX,
    });
  }, []);

  const hideToggleTooltip = useCallback(() => {
    setToggleTooltipPosition(null);
  }, []);

  const closeWorkspaceMenu = useCallback(() => {
    clearWorkspaceMenuCloseTimer();
    setWorkspaceMenuPosition(null);
  }, [clearWorkspaceMenuCloseTimer]);

  const handleNavScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      rememberScrollPosition(event);
      closeHoverMenu();
    },
    [closeHoverMenu, rememberScrollPosition],
  );

  const scheduleWorkspaceMenuClose = useCallback(() => {
    clearWorkspaceMenuCloseTimer();
    workspaceMenuCloseTimerRef.current = window.setTimeout(() => {
      setWorkspaceMenuPosition(null);
    }, SIDENAV_WORKSPACE_MENU_CLOSE_DELAY_MS);
  }, [clearWorkspaceMenuCloseTimer]);

  const toggleWorkspaceMenu = useCallback(() => {
    const rect = workspaceHeaderRef.current?.getBoundingClientRect();
    if (!rect) return;

    clearWorkspaceMenuCloseTimer();
    setWorkspaceMenuPosition((prev) =>
      prev == null
        ? {
            left: rect.left,
            top: rect.bottom + SIDENAV_WORKSPACE_MENU_GUTTER_PX,
            width: rect.width,
          }
        : null,
    );
  }, [clearWorkspaceMenuCloseTimer]);

  const handleWorkspaceHeaderMouseEnter = useCallback(() => {
    clearWorkspaceMenuCloseTimer();
  }, [clearWorkspaceMenuCloseTimer]);

  const handleWorkspaceHeaderMouseLeave = useCallback(() => {
    if (workspaceMenuOpen) scheduleWorkspaceMenuClose();
  }, [scheduleWorkspaceMenuClose, workspaceMenuOpen]);

  useEffect(() => clearHoverMenuCloseTimer, [clearHoverMenuCloseTimer]);
  useEffect(() => clearHoverMenuOpenTimer, [clearHoverMenuOpenTimer]);
  useEffect(() => clearChildPreviewCloseTimer, [clearChildPreviewCloseTimer]);
  useEffect(() => clearWorkspaceMenuCloseTimer, [clearWorkspaceMenuCloseTimer]);
  useEffect(() => {
    if (!showDetailPanes) {
      closeChildPreview();
    }
  }, [closeChildPreview, showDetailPanes]);
  useEffect(() => {
    if (!workspaceMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (navRef.current?.contains(target) || workspaceMenuRef.current?.contains(target)) {
        return;
      }

      closeWorkspaceMenu();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeWorkspaceMenu();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeWorkspaceMenu, workspaceMenuOpen]);

  return (
    <>
      <nav
        ref={navRef}
        aria-label={ariaLabel}
        data-ds-v2={""}
        className={cx(
          SIDENAV_WIDTH,
          themeClass,
          "relative flex flex-col gap-cds-6 overflow-hidden rounded-cds-6",
          SIDENAV_BODY_PADDING_CLASS,
          "pb-cds-12 pt-cds-6",
          themeMode == null
            ? "text-cds-ink-medium dark:text-zinc-300"
            : isDarkMode
              ? "text-zinc-300"
              : "text-cds-ink-medium",
          surfaceClass,
          className,
        )}
        {...rest}
      >
        {scrollTop > 0 && (
          <div
            aria-hidden={true}
            className={cx(
              "pointer-events-none absolute left-0 right-0 top-0 z-[1] h-cds-40",
              "shadow-cds-s dark:shadow-cds-m-dark",
              surface === "elevated"
                ? surfaceClass
                : "bg-cds-surface-active dark:bg-cds-inset-dark",
            )}
          />
        )}
        {showHeader && (
          <SideNavHeaderV2
            headerVariant={headerVariant}
            workspaceHeaderRef={workspaceHeaderRef}
            workspaceLabel={workspaceLabel}
            workspaceIcon={workspaceIcon}
            themeMode={themeMode}
            isDarkMode={isDarkMode}
            backLabel={backLabel}
            backHref={backHref}
            onBackToApp={onBackToApp}
            hasWorkspaceMenu={hasWorkspaceMenu}
            workspaceMenuOpen={workspaceMenuOpen}
            onWorkspaceSelect={onWorkspaceSelect}
            onToggleWorkspaceMenu={toggleWorkspaceMenu}
            onWorkspaceHeaderMouseEnter={handleWorkspaceHeaderMouseEnter}
            onWorkspaceHeaderMouseLeave={handleWorkspaceHeaderMouseLeave}
            showToggleButton={showToggleButton}
            toggleButtonRef={toggleButtonRef}
            toggleButtonIcon={toggleButtonIcon}
            toggleButtonLabel={toggleButtonLabel}
            toggleTooltipId={toggleTooltipId}
            toggleTooltipVisible={toggleTooltipPosition != null}
            onShowToggleTooltip={showToggleTooltip}
            onHideToggleTooltip={hideToggleTooltip}
            onToggleCollapse={onToggleCollapse}
          />
        )}

        {headerAccessory}

        <div
          ref={(node) => {
            scrollRef.current = node;
            sectionReorder.listRef.current = node;
            rowReorder.frameRef.current = node;
          }}
          role={"list"}
          className={cx(
            "min-h-0 -mx-[5px] flex flex-1 flex-col overflow-y-auto overflow-x-hidden px-[5px]",
            // Positioned while sections or rows reorder, so their offsets are measured from it.
            (onReorderSections != null || onArrangeRows != null) && "relative",
            SIDENAV_ITEM_GAP_CLASS,
            SIDENAV_SCROLLBAR_CLASS,
          )}
          onScroll={handleNavScroll}
        >
          {sectionReorder.dropSlot != null && (
            <SideNavDropSlot kind={"section"} {...sectionReorder.dropSlot} />
          )}
          {rowReorder.dropSlot != null && <SideNavDropSlot kind={"row"} {...rowReorder.dropSlot} />}
          {renderedSections.map((section, sectionIndex) => {
            const movable = sectionIndex >= fixedSectionCount;
            const lifted = section.id === sectionReorder.liftedId;
            const sectionHidden = section.hidden === true;
            const collapsed = section.collapsible === true && collapsedSectionIds.has(section.id);
            const itemsId = `${sectionsId}-${section.id}`;
            const rowsArrangeable = rowListIds.has(section.id);
            const renderRow = (
              item: SideNavV2Item<T>,
              dragHandle?: SideNavItemRowProps<T>["dragHandle"],
            ) => (
              <SideNavItemRow
                key={item.id}
                item={item}
                dragHandle={dragHandle}
                activeItemId={activeItemId}
                hoverMenuOpen={hoverMenu?.item.id === item.id}
                hoverMenusDisabled={hoverMenusDisabled}
                sectionHidden={sectionHidden || collapsed}
                currentPath={currentPath}
                onOpenHoverMenu={openHoverMenu}
                onScheduleHoverMenuClose={scheduleHoverMenuClose}
                onOpenChildPreview={openChildPreview}
                onScheduleChildPreviewClose={scheduleChildPreviewClose}
                onCloseChildPreview={closeChildPreview}
                onCloseHoverMenu={closeHoverMenu}
                onBeforeItemSelect={onBeforeItemSelect}
                onItemSelect={onItemSelect}
                onItemActionsMenuOpenChange={onItemActionsMenuOpenChange}
              />
            );
            const itemRows = rowsArrangeable ? (
              <div
                ref={rowReorder.registerList(section.id)}
                role={"presentation"}
                data-sidenav-row-list={""}
                className={cx("flex flex-col", SIDENAV_ITEM_GAP_CLASS)}
              >
                {(rowReorder.orderedLists.get(section.id) ?? section.items).map((item) => (
                  <div
                    key={item.id}
                    ref={rowReorder.registerRow(item.id)}
                    role={"presentation"}
                    data-sidenav-reorderable-row={""}
                    className={cx(
                      // Lifted, it floats over the rows it passes, on the nav's own surface.
                      rowReorder.liftedId === item.id &&
                        cx(
                          "relative z-10 rounded-cds-3 shadow-cds-s dark:shadow-cds-m-dark",
                          surfaceClass,
                          // Leaving for a target outside the nav.
                          rowReorder.externalTargetId != null && "opacity-60",
                        ),
                    )}
                    onClickCapture={rowReorder.onRowClickCapture}
                  >
                    {/* Only the grip drags the row; the rest of it stays a link. */}
                    {renderRow(item, {
                      lifted: rowReorder.liftedId === item.id,
                      onPointerDown: (event) => rowReorder.onRowPointerDown(event, item.id),
                    })}
                  </div>
                ))}
              </div>
            ) : (
              section.items.map((item) => renderRow(item))
            );

            // Reorderable titled sections the caller lets readers edit carry a "…" menu.
            const sectionActions: SideNavV2ItemAction[] =
              movable && section.label && section.fixedRows !== true && !section.collapsible
                ? [
                    ...(onRenameSection == null
                      ? []
                      : [
                          {
                            id: "rename-section",
                            label: "Rename",
                            icon: "edit" as const,
                            onSelect: () => setRenamingSectionId(section.id),
                          },
                        ]),
                    ...(onCreateSection == null
                      ? []
                      : [
                          {
                            id: "new-section",
                            label: "New section below",
                            icon: "add" as const,
                            onSelect: () => {
                              const createdId = onCreateSection(section.id);
                              if (createdId != null) setRenamingSectionId(createdId);
                            },
                          },
                        ]),
                    ...(onDeleteSection == null || section.items.length > 0
                      ? []
                      : [
                          {
                            id: "delete-section",
                            label: "Delete section",
                            icon: "delete" as const,
                            onSelect: () => onDeleteSection(section.id),
                          },
                        ]),
                  ]
                : [];
            const title = section.label ? (
              renamingSectionId === section.id ? (
                <SideNavSectionTitleInput
                  label={section.label}
                  onCommit={(label) => {
                    setRenamingSectionId(null);
                    onRenameSection?.(section.id, label);
                  }}
                  onCancel={() => setRenamingSectionId(null)}
                />
              ) : section.collapsible ? (
                <SideNavCollapsibleSectionTitle
                  label={section.label}
                  collapsed={collapsed}
                  controlsId={itemsId}
                  revealChevronOnHover={section.revealChevronOnHover}
                  besideDragHandle={movable}
                  onToggle={() => toggleSection(section.id)}
                />
              ) : (
                <SideNavSectionTitleV2
                  label={section.label}
                  // Clear of the "…" button and grip that show on hover.
                  className={sectionActions.length > 0 ? "!pr-[44px]" : undefined}
                />
              )
            ) : null;

            // The outer box is the one a drag moves: the inner one's own transitions stay its own.
            return (
              <div
                key={section.id}
                ref={movable ? sectionReorder.registerRow(section.id) : undefined}
                role={"presentation"}
                className={cx(
                  // Lifted, it floats over the sections it passes, on the nav's own surface.
                  lifted &&
                    cx(
                      "relative z-10 rounded-cds-6 shadow-cds-s dark:shadow-cds-m-dark",
                      surfaceClass,
                      // Leaving for a target outside the nav.
                      sectionReorder.externalTargetId != null && "opacity-60",
                    ),
                )}
                onClickCapture={movable ? sectionReorder.onRowClickCapture : undefined}
              >
                <div
                  role={"presentation"}
                  aria-hidden={sectionHidden}
                  className={cx(
                    "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
                    sectionHidden
                      ? "pointer-events-none grid-rows-[0fr] opacity-0"
                      : "grid-rows-[1fr] opacity-100",
                  )}
                >
                  <div
                    className={cx(
                      "min-h-0 -mx-[5px] flex flex-col overflow-hidden px-[5px]",
                      SIDENAV_ITEM_GAP_CLASS,
                      rowDragOverflowClass,
                      // The title field's focus ring reaches past the section's edge.
                      renamingSectionId === section.id && "overflow-visible",
                    )}
                  >
                    {/* The title slides as the rows do when rows enter or leave a section above. */}
                    <div
                      ref={rowsArrangeable ? rowReorder.registerFollower(section.id) : undefined}
                    >
                      {movable && title != null ? (
                        <div className={"group/section-heading relative"}>
                          {title}
                          {renamingSectionId !== section.id && sectionActions.length > 0 && (
                            <SideNavItemActions
                              label={section.label ?? section.id}
                              actions={sectionActions}
                              placement={"section-title"}
                              onOpenChange={onItemActionsMenuOpenChange ?? (() => {})}
                            />
                          )}
                          {renamingSectionId !== section.id && (
                            <SideNavSectionDragHandle
                              lifted={lifted}
                              onPointerDown={(event) =>
                                sectionReorder.onRowPointerDown(event, section.id)
                              }
                            />
                          )}
                        </div>
                      ) : (
                        title
                      )}
                    </div>
                    {section.collapsible ? (
                      <div
                        id={itemsId}
                        className={cx(
                          "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
                          collapsed
                            ? "pointer-events-none grid-rows-[0fr] opacity-0"
                            : "grid-rows-[1fr] opacity-100",
                        )}
                      >
                        <div
                          className={cx(
                            "min-h-0 -mx-[5px] flex flex-col overflow-hidden px-[5px]",
                            SIDENAV_ITEM_GAP_CLASS,
                            rowDragOverflowClass,
                          )}
                        >
                          {itemRows}
                        </div>
                      </div>
                    ) : (
                      itemRows
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {sectionReorder.overlay}
        {rowReorder.overlay}
        {footerAccessory && (
          <div role={"presentation"} className={"shrink-0"}>
            {footerAccessory}
          </div>
        )}
      </nav>
      {hoverMenu != null &&
        typeof document !== "undefined" &&
        createPortal(
          <ViewportAwareFixedMenuV2
            data-sidenav-child-preview-anchor={""}
            className={cx(themeClass, ZIndexClass.NAVIGATION_FLYOUT)}
            left={hoverMenu.left}
            top={hoverMenu.top}
            width={hoverMenu.width}
            onMouseEnter={clearHoverMenuCloseTimer}
            onMouseLeave={scheduleHoverMenuClose}
          >
            <div aria-hidden={true} className={"absolute right-full top-0 h-full w-cds-2"} />
            <MenuContainerV2
              className={
                "w-full max-h-[var(--viewport-aware-fixed-menu-max-height)] overflow-y-auto"
              }
            >
              {hoverMenu.sections.map((section, index) => (
                <MenuSectionV2
                  key={section.id}
                  className={section.label != null ? "border-t-0" : undefined}
                >
                  {index === 0 && hoverMenu.title != null && (
                    <MenuItemV2
                      size={"sm"}
                      variant={"title"}
                      label={hoverMenu.title}
                      showBottomDivider
                    />
                  )}
                  {section.label && (
                    <MenuItemV2
                      size={"sm"}
                      variant={"title"}
                      label={section.label}
                      showBottomDivider
                    />
                  )}
                  {section.items.map((child) => {
                    const childDeprecated = child.deprecationNotice != null;
                    const menuItem = (
                      <MenuItemV2
                        key={child.id}
                        data-testid={`sidebar-${child.id}`}
                        size={"sm"}
                        label={renderSideNavItemLabel(child)}
                        href={childDeprecated ? undefined : child.href}
                        shouldOpenInNewTab={child.shouldOpenInNewTab}
                        leftIcon={child.icon}
                        rightAccessory={renderSideNavCountBadge(
                          child.count,
                          !childDeprecated && child.id === activeItemId,
                        )}
                        disabled={child.disabled || childDeprecated}
                        aria-disabled={childDeprecated || child.disabled || undefined}
                        className={cx(
                          childDeprecated && "!cursor-not-allowed",
                          child.count != null && SIDENAV_RIGHT_AFFORDANCE_PADDING_CLASS,
                          getSideNavItemVisualTreatmentClass(child, redSurfacesSuppressed),
                        )}
                        onMouseEnter={(event) => {
                          if (childDeprecated) return;
                          openChildPreview(child, event.currentTarget);
                        }}
                        onMouseLeave={scheduleChildPreviewClose}
                        onFocus={(event) => {
                          if (childDeprecated) return;
                          openChildPreview(child, event.currentTarget);
                        }}
                        onBlur={scheduleChildPreviewClose}
                        onClick={() => {
                          if (child.disabled || childDeprecated) return;
                          closeHoverMenu();
                          onBeforeItemSelect?.(child);
                          child.onSelect?.();
                          onItemSelect?.(child);
                        }}
                      />
                    );

                    if (child.deprecationNotice == null) {
                      return menuItem;
                    }

                    return (
                      <TooltipV2
                        key={child.id}
                        content={child.deprecationNotice}
                        placement={"right"}
                        persistOnHover={false}
                      >
                        {menuItem}
                      </TooltipV2>
                    );
                  })}
                </MenuSectionV2>
              ))}
            </MenuContainerV2>
          </ViewportAwareFixedMenuV2>,
          overlayPortalRoot ?? document.body,
        )}
      {showDetailPanes &&
        childPreview != null &&
        typeof document !== "undefined" &&
        createPortal(
          <ViewportAwareFixedMenuV2
            className={cx(themeClass, ZIndexClass.NAVIGATION_FLYOUT)}
            left={childPreview.left}
            top={childPreview.top}
            height={childPreview.height}
            onMouseEnter={() => {
              clearHoverMenuCloseTimer();
              clearChildPreviewCloseTimer();
            }}
            onMouseLeave={() => {
              if (hoverMenu != null) {
                scheduleHoverMenuClose();
                return;
              }

              scheduleChildPreviewClose();
            }}
          >
            <div aria-hidden={true} className={"absolute right-full top-0 h-full w-cds-4"} />
            <SideNavChildPreviewPane item={childPreview.item} width={childPreview.width} />
          </ViewportAwareFixedMenuV2>,
          overlayPortalRoot ?? document.body,
        )}
      {workspaceMenuPosition != null &&
        typeof document !== "undefined" &&
        createPortal(
          <ViewportAwareFixedMenuV2
            ref={workspaceMenuRef}
            className={cx(themeClass, ZIndexClass.NAVIGATION_FLYOUT)}
            onMouseEnter={clearWorkspaceMenuCloseTimer}
            onMouseLeave={scheduleWorkspaceMenuClose}
            left={workspaceMenuPosition.left}
            top={workspaceMenuPosition.top}
            width={workspaceMenuPosition.width}
          >
            <MenuContainerV2
              className={cx(
                "w-full",
                // Submenus pop out sideways with `position: absolute`, and an
                // overflow-y-auto ancestor also clips the x axis (overflow-x
                // computes to auto), so the scroll container and nested rows
                // are mutually exclusive. Nesting is what keeps these menus
                // short in the first place, so drop the scroller when present.
                !hasWorkspaceSubmenu &&
                  "max-h-[var(--viewport-aware-fixed-menu-max-height)] overflow-y-auto",
              )}
            >
              <MenuSectionV2>
                {workspaceMenuItems?.map((item) => (
                  <SideNavWorkspaceMenuRow
                    key={item.id}
                    item={item}
                    redSurfacesSuppressed={redSurfacesSuppressed}
                    onCloseMenu={closeWorkspaceMenu}
                  />
                ))}
              </MenuSectionV2>
            </MenuContainerV2>
          </ViewportAwareFixedMenuV2>,
          overlayPortalRoot ?? document.body,
        )}
      {toggleTooltipPosition != null &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={cx(
              themeClass,
              "pointer-events-none fixed -translate-x-1/2",
              ZIndexClass.TOOLTIP,
            )}
            style={{ left: toggleTooltipPosition.left, top: toggleTooltipPosition.top }}
          >
            <SideNavToggleTooltip
              id={toggleTooltipId}
              label={toggleTooltipLabel}
              shortcut={toggleTooltipShortcut}
            />
          </div>,
          overlayPortalRoot ?? document.body,
        )}
    </>
  );
};

const SideNavChildPreviewPane = <T extends string>({
  item,
  width,
}: {
  item: SideNavV2Item<T>;
  width: number;
}) => {
  return (
    <aside
      role={"tooltip"}
      aria-label={`${item.label} preview`}
      style={{ width }}
      className={cx(
        "flex h-full flex-col overflow-hidden rounded-cds-6 border-[1px]",
        "p-cds-10",
        "dark:border-white/[0.08] dark:bg-cds-elevated-dark dark:text-zinc-200 shadow-cds-m-dark",
        "border-cds-surface-border bg-cds-surface-default text-cds-ink shadow-cds-m",
      )}
    >
      <div className={"flex items-center gap-cds-8"}>
        {renderSideNavIcon(
          item.icon,
          cx("shrink-0", "dark:!text-zinc-300", "!text-cds-ink-secondary"),
        )}
        <div
          className={cx(
            "min-w-0 flex-1 truncate text-cds-md font-normal ",
            "dark:text-zinc-50",
            "text-cds-ink-strong",
          )}
        >
          {item.label}
        </div>
      </div>
      <p className={"mt-cds-6 text-cds-sm dark:text-zinc-400 text-cds-ink-muted"}>
        {getSideNavItemDescription(item)}
      </p>
      <dl className={"mt-cds-8 flex items-center justify-between gap-cds-8 text-cds-xs"}>
        <dt className={"dark:text-zinc-500 text-cds-ink-subtle"}>Type</dt>
        <dd className={"truncate font-medium dark:text-zinc-300 text-cds-ink-secondary"}>Page</dd>
      </dl>
    </aside>
  );
};

const SideNavToggleTooltip: React.FC<{
  id: string;
  label: string;
  shortcut: SidebarToggleShortcutHint;
}> = ({ id, label, shortcut }) => {
  return <SidebarToggleShortcutTooltip id={id} label={label} shortcut={shortcut} />;
};
